'use client';

import React, {useEffect, useState} from 'react';
import {Box, Link, List, ListItem, Typography} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faBookFont,
    faCopyright,
    faFileCircleCheck,
    faThumbTack
} from "@fortawesome/pro-duotone-svg-icons";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";

const ArticleOutlines = (
    {
        hasAbstract = false,
        hasAcknowledgments = false,
        hasFootnotes = false,
        hasReferences = false,
        hasCopyright = false,
    }: {
        hasAbstract?: boolean;
        hasAcknowledgments?: boolean;
        hasFootnotes?: boolean;
        hasReferences?: boolean;
        hasCopyright?: boolean;
    }
) => {
    const [headings, setHeadings] = useState<{ id: string; label: string }[]>([]);

    useEffect(() => {
        const articleBody = document.getElementById('article-body');
        if (articleBody) {
            const headingElements = articleBody.querySelectorAll("h3");
            const extractedHeadings = Array.from(headingElements).map((heading: any, index: number) => {
                heading.id = `section-${index}`;
                const id = `section-${index}`;
                return {id, label: heading.textContent || ""};
            });
            setHeadings(extractedHeadings);
        }
    }, []);

    const handleScrollToSection = (
        event: React.MouseEvent<HTMLAnchorElement>,
        sectionId: string
    ) => {
        event.preventDefault();
        const section = document.querySelector(`#${sectionId}`);
        if (section) {
            section.scrollIntoView({behavior: 'smooth'});
        }
    };

    const listStyle = {
        '& .MuiListItem-root': {
            pt: '0',
            pb: '.25rem',
            fontSize: '.687rem',
            'a': {
                transition: 'all ease-in-out .3s',
                display: 'flex',
                alignItems: 'flex-start',
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': {
                    cursor: 'pointer',
                    transform: 'scale(1.03)',
                },
                'span:nth-of-type(1)': {
                    color: 'primary.main',
                    fontSize: '.875rem',
                    mr: .25
                },
                'span:nth-of-type(2)': {
                    fontSize: '1rem'
                }
            },
        },
    };

    return (
        <Box bgcolor={"custom.middleGray"} borderRadius={2} p={1} width={"100%"}>
            <Typography
                variant={"h3"}
                color={"primary"}
                fontSize={'1.25rem'}
                fontWeight={"bold"}
            >
                Outlines
            </Typography>
            <List sx={listStyle}>
                {
                    hasAbstract && (
                        <ListItem>
                            <Link
                                style={{display: 'flex', alignItems: 'baseline'}}
                                onClick={(event) => {
                                    handleScrollToSection(event, 'article-abstract');
                                }}
                            >
                                <SpanTypography>
                                    <FontAwesomeIcon icon={faBookFont}/>
                                </SpanTypography>
                                <SpanTypography>
                                    Abstract
                                </SpanTypography>
                            </Link>
                        </ListItem>
                    )
                }
                {
                    headings.map((heading, index) => (
                        <ListItem key={`outline-${index}`}>
                            <Link onClick={(event) => handleScrollToSection(event, heading.id)}>
                                <Typography>
                                    {heading.label}
                                </Typography>
                            </Link>
                        </ListItem>
                    ))
                }
                {
                    hasAcknowledgments && (
                        <ListItem>
                            <Link
                                onClick={(event) => handleScrollToSection(event, 'article-acknowledgments')}
                            >
                                <SpanTypography>
                                    <FontAwesomeIcon icon={faFileCircleCheck}/>
                                </SpanTypography>
                                <SpanTypography>
                                    Acknowledgments
                                </SpanTypography>
                            </Link>
                        </ListItem>
                    )
                }
                {
                    hasFootnotes && (
                        <ListItem>
                            <Link
                                onClick={(event) => handleScrollToSection(event, 'article-footnotes')}
                            >
                                <SpanTypography>
                                    <FontAwesomeIcon icon={faThumbTack}/>
                                </SpanTypography>
                                <SpanTypography>
                                    Footnotes
                                </SpanTypography>
                            </Link>
                        </ListItem>
                    )
                }
                {
                    hasReferences && (
                        <ListItem>
                            <Link
                                onClick={(event) => handleScrollToSection(event, 'article-references')}
                            >
                                <SpanTypography>
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare}/>
                                </SpanTypography>
                                <SpanTypography>
                                    References
                                </SpanTypography>
                            </Link>
                        </ListItem>
                    )
                }
                {
                    hasCopyright && (
                        <ListItem>
                            <Link
                                onClick={(event) => handleScrollToSection(event, 'article-copyright')}
                            >
                                <SpanTypography>
                                    <FontAwesomeIcon icon={faCopyright}/>
                                </SpanTypography>
                                <SpanTypography>
                                    Copyright
                                </SpanTypography>
                            </Link>
                        </ListItem>
                    )
                }
            </List>
        </Box>
    );
};

export default ArticleOutlines;
